import type { Customer } from "@/types"
import LoggedOutRoute from "@/components/navigation/LoggedOutRoute"
import { IS_ROOT } from "@/components/utils/CustomerPermissions"
import ErrorBoundary from "@/components/utils/ErrorBoundary"
import GetLoadable from "@/components/utils/GetLoadable"
import PermissionsRoute from "@/components/utils/PermissionsRoute"
import ReactLazyPreload from "@/components/utils/ReactLazyPreload"
import { toastActions } from "@/components/utils/toast"
import { lazy, useEffect } from "react"
import { Navigate, Route, Routes } from "react-router-dom"

const LoadableLoginFormContainer = GetLoadable(
  lazy(() => import("@/components/access/LoginFormContainer")),
)
const LoadableRequestAccessFormContainer = GetLoadable(
  lazy(() => import("@/components/access/RequestAccessFormContainer")),
)
const LoadableChangePasswordActionContainer = GetLoadable(
  lazy(() => import("@/components/access/ChangePasswordActionContainer")),
)
const LoadableForgotPasswordActionContainer = GetLoadable(
  lazy(() => import("@/components/access/ForgotPasswordFormContainer")),
)
const LoadableLoggedOutMessagePage = GetLoadable(
  lazy(() => import("@/components/navigation/LoggedOutMessagePage")),
)
const LoadableCustomerCreationPage = GetLoadable(
  lazy(() => import("@/components/customer/CustomerCreationPage")),
)

const PreLoadAccess = ReactLazyPreload(
  () => import("@/components/access/RequestAccessFormContainer"),
)
const PreLoadForgotPassword = ReactLazyPreload(
  () => import("@/components/access/ForgotPasswordFormContainer"),
)

const PreLoadLogin = ReactLazyPreload(() => import("@/components/access/LoginFormContainer"))

const EmailConfirmed = () => {
  useEffect(() => {
    toastActions.success("Email address confirmed!")
  }, [])

  return <Navigate to="/" replace />
}

const preloadForgot = () => PreLoadForgotPassword.preload?.()
const preloadAccess = () => PreLoadAccess.preload?.()
const preloadLogin = () => PreLoadLogin.preload?.()

const ticketIdAllowedIf = (c: Customer | null) => IS_ROOT(c) || c?.id === 14

const LoggedOutRoutes = () => {
  return (
    <ErrorBoundary>
      <div className="grow">
        <Routes>
          <Route
            path="/login/*"
            element={
              <LoggedOutRoute>
                <LoadableLoginFormContainer
                  preLoadAccess={preloadAccess}
                  preLoadForgot={preloadForgot}
                />
              </LoggedOutRoute>
            }
          />
          <Route
            path="/requestAccess/*"
            element={
              <LoggedOutRoute>
                <LoadableRequestAccessFormContainer preLoadLogin={preloadLogin} />
              </LoggedOutRoute>
            }
          />
          <Route
            path="/networks/:ticketId/*"
            element={
              <PermissionsRoute
                allowedIf={ticketIdAllowedIf}
                component={() => <LoadableCustomerCreationPage />}
              />
            }
          />
          <Route
            path="/resetPassword/:ticketId/*"
            element={<LoadableChangePasswordActionContainer />}
          />
          <Route
            path="/createAccount/:ticketId/*"
            element={<LoadableChangePasswordActionContainer createAccountPage />}
          />
          <Route
            path="/forgotPassword/*"
            element={<LoadableForgotPasswordActionContainer preloadLogin={preloadLogin} />}
          />
          <Route
            path="/message/*"
            element={
              // @ts-expect-error legacy prop mismatch
              <LoadableLoggedOutMessagePage preloadLogin={preloadLogin} />
            }
          />
          <Route path="/emailConfirmed/*" element={<EmailConfirmed />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </ErrorBoundary>
  )
}

export default LoggedOutRoutes
